import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  blobErrorText,
  useGetPtoRdChangeDocQuery,
  useLazyDownloadPtoRdChangeDocFileQuery,
  useListPtoRdChangeDocsQuery,
  useWithdrawPtoRdChangeDocMutation,
  useWithdrawPtoVolumeEditMutation,
  type PtoRdChangeDoc,
  type PtoRdDocument,
  type PtoRdEditsReason,
  type PtoVolumeEdit,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import {
  AddChangePositionForm,
  ChangeDocUploadDialog,
  ChangeDocVersionDialog,
  ReminderNote,
} from './ChangeDocForms';
import { dateRu, dateTimeRu, fileSizeText, statusLabel, valuesText } from './logic';
import { useRdVolumeEditAccess } from './useRdVolumeEditAccess';
import { Buttons, Done, ErrorText, Muted, ReasonDialog, saveBlob } from './ui';

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Card = styled.article<{ $withdrawn: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  opacity: ${({ $withdrawn }) => ($withdrawn ? 0.75 : 1)};
  font-size: 13.5px;
  line-height: 1.55;
`;

const CardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 14.5px;
  overflow-wrap: anywhere;
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  margin-top: ${({ theme }) => theme.spacing(2)};

  th,
  td {
    text-align: left;
    padding: 5px 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }
  th {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 600;
    white-space: nowrap;
  }
`;

const H4 = styled.h4`
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  font-size: 13.5px;
`;

const LinkButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
`;

function kindText(kind: string, kinds: readonly PtoRdEditsReason[]): string {
  return kinds.find((k) => k.code === kind)?.label ?? kind;
}

/** Скачать файл документа (версия или текущий). */
function useDownload() {
  const [fetchFile, { isFetching }] = useLazyDownloadPtoRdChangeDocFileQuery();
  const [error, setError] = useState<string | null>(null);
  async function download(id: string, filename: string, version?: number) {
    setError(null);
    try {
      saveBlob(await fetchFile({ id, version }).unwrap(), filename);
    } catch (e) {
      setError(await blobErrorText(e, 'Файл документа не скачан.'));
    }
  }
  return { download, downloading: isFetching, error };
}

/** Позиция (Б) документа: значение, комплект и ревизия, статус, «Отозвать». */
function PositionRow({ e, canEdit }: { e: PtoVolumeEdit; canEdit: boolean }) {
  const [withdraw, { isLoading }] = useWithdrawPtoVolumeEditMutation();
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = ['active', 'not_in_revision', 'needs_decision'].includes(e.status);
  return (
    <tr>
      <td>{valuesText(e.value)}</td>
      <td>
        {e.document_code ?? '—'}
        {e.revision_no !== null ? `, ревизия ${e.revision_no}` : ''}
      </td>
      <td>
        {statusLabel(e.status)}
        {e.closed_reason && <Muted>{e.closed_reason}</Muted>}
      </td>
      <td>
        {e.created_by_name ?? '—'}, {dateRu(e.created_at)}
      </td>
      <td>
        {canEdit && open && (
          <Button $variant="ghost" $size="sm" onClick={() => setAsking(true)}>
            Отозвать
          </Button>
        )}
        {asking && (
          <ReasonDialog
            title="Отозвать позицию по документу изменения"
            warning="Позиция выйдет из сумм и останется в истории и журнале. Удаления нет."
            confirmLabel="Отозвать"
            busy={isLoading}
            error={error}
            onClose={() => setAsking(false)}
            onConfirm={(reason) => {
              setError(null);
              withdraw({ id: e.id, reason })
                .unwrap()
                .then(() => setAsking(false))
                .catch((err: unknown) => setError(apiErrorText(err, 'Позиция не отозвана.')));
            }}
          />
        )}
      </td>
    </tr>
  );
}

/** Карточка документа: версии файла, позиции, «Внести материал», «Новая
 *  версия», «Отозвать». */
function ChangeDocDetailView({
  doc,
  canEdit,
  maxBytes,
}: {
  doc: PtoRdChangeDoc;
  canEdit: boolean;
  maxBytes: number;
}) {
  const { data, isLoading, error } = useGetPtoRdChangeDocQuery(doc.id);
  const { download, downloading, error: downloadError } = useDownload();
  const [withdrawDoc, { isLoading: withdrawing }] = useWithdrawPtoRdChangeDocMutation();
  const [adding, setAdding] = useState(false);
  const [versioning, setVersioning] = useState(false);
  const [asking, setAsking] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const active = doc.status === 'active';
  if (isLoading) return <Muted>Загрузка документа…</Muted>;
  if (error || !data)
    return <ErrorText>{apiErrorText(error, 'Не удалось загрузить документ.')}</ErrorText>;
  return (
    <div>
      <H4>Версии файла</H4>
      <Table>
        <thead>
          <tr>
            <th>Версия</th>
            <th>Файл</th>
            <th>Загрузил</th>
            <th>Комментарий</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.files.map((f) => (
            <tr key={f.id}>
              <td>
                {f.version}
                {f.version === doc.current_version ? ' (текущая)' : ''}
              </td>
              <td>
                {f.filename} · {fileSizeText(f.size_bytes)}
                <Muted title={f.sha256}>sha256 {f.sha256.slice(0, 16)}…</Muted>
              </td>
              <td>
                {f.uploaded_by_name ?? '—'}, {dateTimeRu(f.uploaded_at)}
              </td>
              <td>{f.comment ?? ''}</td>
              <td>
                <LinkButton
                  type="button"
                  disabled={downloading}
                  onClick={() => void download(doc.id, f.filename, f.version)}
                >
                  Скачать
                </LinkButton>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {downloadError && <ErrorText>{downloadError}</ErrorText>}

      <H4>Позиции по документу ({data.positions.length})</H4>
      {data.positions.length === 0 ? (
        <Muted>Позиций пока нет.</Muted>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Материал</th>
              <th>Комплект</th>
              <th>Статус</th>
              <th>Внёс</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.positions.map((e) => (
              <PositionRow key={e.id} e={e} canEdit={canEdit} />
            ))}
          </tbody>
        </Table>
      )}

      {canEdit && active && (
        <Buttons style={{ marginTop: 12 }}>
          {!adding && (
            <Button $variant="primary" $size="sm" onClick={() => setAdding(true)}>
              Внести материал
            </Button>
          )}
          <Button $variant="secondary" $size="sm" onClick={() => setVersioning(true)}>
            Новая версия файла
          </Button>
          <Button $variant="ghost" $size="sm" onClick={() => setAsking(true)}>
            Отозвать документ
          </Button>
        </Buttons>
      )}
      {adding && (
        <div style={{ marginTop: 12 }}>
          <AddChangePositionForm doc={doc} onDone={() => setAdding(false)} />
        </div>
      )}
      {versioning && (
        <ChangeDocVersionDialog
          doc={doc}
          maxBytes={maxBytes}
          onClose={() => setVersioning(false)}
        />
      )}
      {asking && (
        <ReasonDialog
          title={`Отозвать документ № ${doc.number}`}
          warning="Документ не удаляется: он и его позиции остаются в истории и журнале, действующие позиции выходят из сумм."
          confirmLabel="Отозвать документ"
          busy={withdrawing}
          error={withdrawError}
          onClose={() => setAsking(false)}
          onConfirm={(reason) => {
            setWithdrawError(null);
            withdrawDoc({ id: doc.id, reason })
              .unwrap()
              .then(() => setAsking(false))
              .catch((e: unknown) => setWithdrawError(apiErrorText(e, 'Документ не отозван.')));
          }}
        />
      )}
    </div>
  );
}

function DocCard({
  doc,
  kinds,
  canEdit,
  maxBytes,
  expanded,
  onToggle,
}: {
  doc: PtoRdChangeDoc;
  kinds: readonly PtoRdEditsReason[];
  canEdit: boolean;
  maxBytes: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const withdrawn = doc.status === 'withdrawn';
  const { download, downloading, error } = useDownload();
  return (
    <Card $withdrawn={withdrawn}>
      <CardHead>
        <Title>
          {kindText(doc.kind, kinds)} № {doc.number} от {dateRu(doc.doc_date)} — «{doc.title}»
        </Title>
        <span>
          {withdrawn ? (
            <Badge $tone="neutral">отозван</Badge>
          ) : (
            <Badge $tone="success">действует</Badge>
          )}{' '}
          {doc.reminder_due && !withdrawn && <Badge $tone="warning">30 дней без ревизии</Badge>}
        </span>
      </CardHead>
      <div>
        <Label>Комплекты:</Label> {doc.document_codes.join(', ') || '—'}
      </div>
      <div>
        <Label>Согласовано Заказчиком:</Label> {doc.approval_name}, {doc.approval_position},{' '}
        {dateRu(doc.approval_date)}
        {doc.approval_confirmed ? ' · отметка согласования есть в файле' : ''}
      </div>
      <div>
        <Label>Обоснование крайней необходимости:</Label> {doc.urgency}
      </div>
      <div>
        <Label>Файл:</Label>{' '}
        {doc.file_name ? (
          <LinkButton
            type="button"
            disabled={downloading}
            onClick={() => void download(doc.id, doc.file_name ?? 'document')}
          >
            {doc.file_name}
          </LinkButton>
        ) : (
          '—'
        )}
        {doc.file_size !== null ? ` · ${fileSizeText(doc.file_size)}` : ''} · версия{' '}
        {doc.current_version}
      </div>
      <div>
        <Label>Позиций:</Label> в суммах {doc.positions_active} из {doc.positions_total}
        {' · '}
        <Label>Загрузил:</Label> {doc.created_by_name ?? '—'}, {dateTimeRu(doc.created_at)}
      </div>
      {doc.comment && (
        <div>
          <Label>Комментарий:</Label> {doc.comment}
        </div>
      )}
      {withdrawn && (
        <div>
          <Label>Отозван:</Label> {doc.withdrawn_reason ?? ''} ({doc.withdrawn_by_name ?? '—'},{' '}
          {dateTimeRu(doc.withdrawn_at)})
        </div>
      )}
      {doc.reminder_due && !withdrawn && <ReminderNote />}
      {error && <ErrorText>{error}</ErrorText>}
      <Buttons style={{ marginTop: 8 }}>
        <Button $variant="ghost" $size="sm" onClick={onToggle} aria-expanded={expanded}>
          {expanded ? 'Свернуть' : 'Версии и позиции'}
        </Button>
      </Buttons>
      {expanded && <ChangeDocDetailView doc={doc} canEdit={canEdit} maxBytes={maxBytes} />}
    </Card>
  );
}

/**
 * Реестр документов изменения РД, согласованных Заказчиком (№8, Б) на экране
 * «Изменения РД и запросы SQ»: загрузка со всеми обязательными реквизитами,
 * версии файла, позиции «ДИ», напоминание через 30 дней без ревизии,
 * «Отозвать» с причиной — кнопки «Удалить» нет.
 */
export function ChangeDocsPanel({
  projectId,
  documents,
}: {
  projectId: string;
  documents: readonly PtoRdDocument[];
}) {
  const rights = useRdVolumeEditAccess(projectId);
  const [documentId, setDocumentId] = useState('');
  const { data, isLoading, error } = useListPtoRdChangeDocsQuery(
    { projectId, document_id: documentId || undefined },
    { skip: !projectId },
  );
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const kinds = data?.kinds ?? rights.access?.change_doc_kinds ?? [];
  const maxBytes = rights.access?.max_change_doc_bytes ?? 20 * 1024 * 1024;
  const items = data?.items ?? [];
  const sortedDocs = useMemo(
    () => [...documents].sort((a, b) => a.code.localeCompare(b.code, 'ru')),
    [documents],
  );

  return (
    <div>
      <Head>
        <div>
          <h3 style={{ margin: 0 }}>Документы изменения РД</h3>
          <Muted>
            Материал, которого нет в РД, — только по документу, согласованному Заказчиком, в крайней
            необходимости и до выдачи ревизии. Удалить нельзя — «Отозвать» с причиной.
          </Muted>
        </div>
        {rights.canEdit && (
          <Button $variant="primary" $size="sm" onClick={() => setUploading(true)}>
            Загрузить документ изменения
          </Button>
        )}
      </Head>
      {!rights.enabled && (
        <Muted style={{ marginBottom: 12 }}>
          Правки РД для этого проекта не включены
          {rights.mode === 'test' ? ' (сейчас — только проект «Тест»)' : ''}: документы изменения
          вносить нельзя.
        </Muted>
      )}
      {rights.enabled && !rights.canEdit && (
        <Muted style={{ marginBottom: 12 }}>
          Загружать документы и вносить материал по ним может роль не ниже «Инженер+».
        </Muted>
      )}
      <Select
        aria-label="Комплект РД"
        value={documentId}
        onChange={(e) => setDocumentId(e.target.value)}
        style={{ maxWidth: 420, marginBottom: 12 }}
      >
        <option value="">Все комплекты</option>
        {sortedDocs.map((d) => (
          <option key={d.id} value={d.id}>
            {d.code} · {d.title}
          </option>
        ))}
      </Select>
      {created && (
        <Done role="status" style={{ marginBottom: 12 }}>
          {created}
        </Done>
      )}
      {isLoading ? (
        <Muted>Загрузка…</Muted>
      ) : error ? (
        <ErrorText>{apiErrorText(error, 'Не удалось загрузить документы изменения.')}</ErrorText>
      ) : items.length === 0 ? (
        <Muted>Документов изменения нет.</Muted>
      ) : (
        items.map((doc) => (
          <DocCard
            key={doc.id}
            doc={doc}
            kinds={kinds}
            canEdit={rights.canEdit}
            maxBytes={maxBytes}
            expanded={expanded === doc.id}
            onToggle={() => setExpanded((x) => (x === doc.id ? null : doc.id))}
          />
        ))
      )}
      {uploading && (
        <ChangeDocUploadDialog
          projectId={projectId}
          documents={sortedDocs}
          kinds={kinds}
          maxBytes={maxBytes}
          onClose={() => setUploading(false)}
          onCreated={(detail) => {
            setUploading(false);
            setExpanded(detail.doc.id);
            setCreated(
              `Документ № ${detail.doc.number} загружен. Откройте «Версии и позиции» — «Внести материал».`,
            );
          }}
        />
      )}
    </div>
  );
}
